import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { formatPrice, calculateSavingsPercentage } from '@/lib/utils';
import { X, Save, Upload } from 'lucide-react';
import type { Product, UpdateProduct } from '@shared/schema';

interface EditProductModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: number, updates: UpdateProduct) => void;
}

export function EditProductModal({ product, isOpen, onClose, onSave }: EditProductModalProps) {
  const [formData, setFormData] = useState<Partial<Product>>({});

  useEffect(() => {
    if (product) {
      setFormData({
        description: product.description,
        chineseName: product.chineseName,
        detailedDescription: product.detailedDescription,
        size: product.size,
        retailPrice: product.retailPrice,
        ourPrice: product.ourPrice,
        category: product.category,
        active: product.active,
        imageUrl: product.imageUrl
      });
    }
  }, [product]);

  if (!isOpen || !product) return null;

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 512;
          canvas.height = 512;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.error('Failed to get canvas context');
            return;
          }
          
          // Calculate dimensions to maintain aspect ratio
          const aspectRatio = img.width / img.height;
          let drawWidth = 512;
          let drawHeight = 512;
          let offsetX = 0;
          let offsetY = 0;
          
          if (aspectRatio > 1) {
            drawHeight = 512 / aspectRatio;
            offsetY = (512 - drawHeight) / 2;
          } else {
            drawWidth = 512 * aspectRatio;
            offsetX = (512 - drawWidth) / 2;
          }
          
          // Fill background
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, 512, 512);
          
          // Draw image centered and scaled
          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          
          // Convert to data URL with JPEG compression
          const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          setFormData({ ...formData, imageUrl: resizedDataUrl });
        };
        if (typeof e.target?.result === 'string') {
          img.src = e.target.result;
        } else {
          console.error('Invalid file data');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    // Extract only the fields that can be updated
    const updateData: UpdateProduct = {
      description: formData.description,
      chineseName: formData.chineseName || null,
      detailedDescription: formData.detailedDescription || null,
      size: formData.size,
      retailPrice: typeof formData.retailPrice === 'string' 
        ? (isNaN(parseFloat(formData.retailPrice)) ? 0 : parseFloat(formData.retailPrice))
        : (formData.retailPrice || 0),
      ourPrice: typeof formData.ourPrice === 'string' 
        ? (isNaN(parseFloat(formData.ourPrice)) ? 0 : parseFloat(formData.ourPrice))
        : (formData.ourPrice || 0),
      category: formData.category,
      active: formData.active,
      imageUrl: formData.imageUrl || null
    };
    onSave(product.id, updateData);
  };

  const savings = formData.retailPrice && formData.ourPrice
    ? calculateSavingsPercentage(formData.retailPrice, formData.ourPrice)
    : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="w-full max-w-lg my-8"
      >
        <Card className="bg-white max-h-[90vh] flex flex-col">
          <CardHeader className="border-b pb-4 flex-shrink-0">
            <div className="flex justify-between items-center">
              <CardTitle className="text-2xl">Edit Product</CardTitle>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4 overflow-y-auto flex-1">
            {/* Product Name */}
            <div>
              <Label htmlFor="description">Product Name</Label>
              <Input
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="text-lg font-medium"
              />
            </div>

            {/* Chinese Name */}
            <div>
              <Label htmlFor="chineseName">Chinese Name (中文名称)</Label>
              <Input
                id="chineseName"
                value={formData.chineseName || ''}
                onChange={(e) => setFormData({ ...formData, chineseName: e.target.value })}
                placeholder="输入中文名称"
                className="text-lg"
              />
            </div>

            {/* Size and Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="size">Size/Unit</Label>
                <Input
                  id="size"
                  value={formData.size || ''}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={formData.category || ''} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="produce">Produce</SelectItem>
                    <SelectItem value="meat-beef">Meat - Beef</SelectItem>
                    <SelectItem value="meat-chicken">Meat - Chicken</SelectItem>
                    <SelectItem value="meat-pork">Meat - Pork</SelectItem>
                    <SelectItem value="meat-hotpot">Meat - Hot Pot</SelectItem>
                    <SelectItem value="seafood">Seafood</SelectItem>
                    <SelectItem value="groceries">Groceries</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="retailPrice">Retail Price ($)</Label>
                  <Input
                    id="retailPrice"
                    type="number"
                    step="0.01"
                    value={formData.retailPrice || ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setFormData({ ...formData, retailPrice: isNaN(val) ? 0 : val });
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="ourPrice">Our Price ($)</Label>
                  <Input
                    id="ourPrice"
                    type="number"
                    step="0.01"
                    value={formData.ourPrice || ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setFormData({ ...formData, ourPrice: isNaN(val) ? 0 : val });
                    }}
                  />
                </div>
              </div>
              
              {/* Live Savings Preview */}
              {formData.retailPrice && formData.ourPrice && (
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-600 mb-1">Customer Savings</div>
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-red-500 line-through text-xl">
                      {formatPrice(formData.retailPrice)}
                    </span>
                    <span className="text-3xl font-bold text-emerald-600">
                      {formatPrice(formData.ourPrice)}
                    </span>
                    <span className="bg-emerald-600 text-white px-3 py-1 rounded-full font-bold">
                      Save {savings}%
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Product Image */}
            <div>
              <Label>Product Image</Label>
              <div className="mt-2">
                {formData.imageUrl ? (
                  <div className="space-y-2">
                    <div className="relative inline-block">
                      <img 
                        src={formData.imageUrl} 
                        alt="Product preview" 
                        className="w-32 h-32 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, imageUrl: null })}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      {formData.imageUrl.startsWith('data:') ? 'Uploaded image' : formData.imageUrl}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600">Choose one option:</div>
                    
                    <div className="border rounded-lg p-3 space-y-2">
                      <div className="font-medium text-sm">Option 1: Upload Image</div>
                      <label className="w-full h-24 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-colors">
                        <Upload className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1">Click to upload (max 5MB)</span>
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                    
                    <div className="border rounded-lg p-3 space-y-2">
                      <div className="font-medium text-sm">Option 2: Image URL</div>
                      <Input
                        type="text"
                        value=""
                        onChange={(e) => {
                          const url = e.target.value.trim();
                          if (url) {
                            setFormData({ ...formData, imageUrl: url });
                          }
                        }}
                        placeholder="/images/products/filename.jpg"
                        className="text-sm"
                      />
                      <p className="text-xs text-gray-500">
                        Use existing images from /images/products/
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Detailed Description */}
            <div>
              <Label htmlFor="detailedDescription">Detailed Description (Optional)</Label>
              <Textarea
                id="detailedDescription"
                value={formData.detailedDescription || ''}
                onChange={(e) => setFormData({ ...formData, detailedDescription: e.target.value })}
                placeholder="Enter detailed product description (supports Markdown or HTML)"
                className="min-h-[100px] text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Supports Markdown or HTML formatting</p>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="active" className="text-base">Active</Label>
              <Switch
                id="active"
                checked={formData.active ?? true}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700" 
                onClick={handleSave}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}